/**
 * Every time the dashboard shows is Malaysia time.
 *
 * The warehouse is in Malaysia and so is everyone reading this screen. Storage is UTC (`timezone:
 * 'Z'` on both MySQL pools, per CLAUDE.md §6), so a timestamp that reaches the browser unconverted
 * reads eight hours early — a 9am scan shows as 1am, which is not a cosmetic problem: it is the
 * difference between "this arrived before the cut-off" and "it did not".
 *
 * Two shapes arrive from the API and BOTH mean UTC:
 *
 *   '2026-08-13 03:30:00.109'    MySQL DATETIME(3), via mysql2 `dateStrings: true` — no zone marker
 *   '2026-08-13T03:30:00.000Z'   a JS Date that went through JSON.stringify
 *
 * The first is the dangerous one. `new Date('2026-08-13 03:30:00')` is interpreted in the
 * BROWSER's zone, so it happens to look right on a laptop set to Malaysia and wrong on one set to
 * anything else. `parseUtc()` pins the zone instead of trusting the machine.
 *
 * A bare `YYYY-MM-DD` is never an instant. Those are SQL Accounting business dates (`docdate`) and
 * balance snapshot dates — a calendar day the accountant chose, with no time and no zone. Shifting
 * one would move a document to the wrong day, so they pass through untouched. See `businessDate`.
 */

export const MY_TZ = 'Asia/Kuala_Lumpur';

/** For subtitles and column headers, so nobody has to guess which clock a screen is on. */
export const MYT = 'MYT';

/** A calendar date with no time component — a business date, not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Already carries a zone: trailing Z, or a ±HH:MM / ±HHMM offset. */
const ZONED = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

export type Timestamp = string | number | Date | null | undefined;

/**
 * Parse an API timestamp into an instant, treating a zone-less string as UTC.
 *
 * Returns null for anything unparseable — a formatter that throws would blank a whole table over
 * one bad row, and a warehouse supervisor cannot act on a stack trace.
 */
export function parseUtc(value: Timestamp): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const raw = value.trim();
  if (raw === '' || raw === '—') return null;

  // A bare date has no instant to convert. Midnight UTC would be 8am the same day in Malaysia,
  // which happens to be harmless — but midnight Malaysia is 4pm the PREVIOUS day in UTC, and the
  // asymmetry is exactly the kind of thing that shifts a document by a day. Refuse instead.
  if (DATE_ONLY.test(raw)) return null;

  // MySQL's ' ' separator is not ISO-8601; every engine accepts 'T'.
  const normalised = raw.replace(' ', 'T');
  const d = new Date(ZONED.test(normalised) ? normalised : `${normalised}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Malaysia-time components of an instant.
 *
 * `hourCycle: 'h23'` rather than `hour12: false` — the latter still renders midnight as "24" on
 * some engines, which would print 2026-08-13 24:05 for a five-past-midnight scan.
 */
function partsInMY(d: Date): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: MY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) out[p.type] = p.value;
  return out;
}

/** What an empty cell shows. Matches the em-dash the tables already use, and sorts last. */
const NONE = '—';

/**
 * `2026-08-13 11:30` in Malaysia time — the default for any instant on a table.
 *
 * Deliberately ISO-ordered rather than "13 Aug 2026". The tables sort by rendered text, and
 * year-first is the only format that sorts chronologically as a string.
 */
export function dateTime(value: Timestamp, fallback = NONE): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  const p = partsInMY(d);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/** `2026-08-13 11:30:25` — for audit trails, where the second is the point. */
export function dateTimeSeconds(value: Timestamp, fallback = NONE): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  const p = partsInMY(d);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

/** The Malaysian calendar day an instant fell on: `2026-08-13`. */
export function dateOnly(value: Timestamp, fallback = NONE): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  const p = partsInMY(d);
  return `${p.year}-${p.month}-${p.day}`;
}

/** `11:30` — when the day is already established by the row or the panel. */
export function timeOnly(value: Timestamp, fallback = NONE): string {
  const d = parseUtc(value);
  if (!d) return fallback;
  const p = partsInMY(d);
  return `${p.hour}:${p.minute}`;
}

/**
 * A SQL Accounting business date, rendered as it is stored.
 *
 * NOT converted. `docdate` and `snapshot_date` are calendar days a human chose; they have no time
 * and no zone, and treating one as an instant is how a receipt ends up dated the day before.
 */
export function businessDate(value: Timestamp, fallback = NONE): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (value instanceof Date || typeof value === 'number') return dateOnly(value, fallback);
  const raw = value.trim();
  if (raw === '' || raw === NONE) return fallback;
  // A DATE column arrives as 'YYYY-MM-DD'; a DATETIME that is really a business date arrives with
  // a time we do not want to show. Either way the first ten characters are the day.
  return DATE_ONLY.test(raw) ? raw : raw.slice(0, 10);
}

/**
 * A timestamp with its full precision on hover.
 *
 * The tables render minutes; someone reconciling a scan against a PDA screen needs the second.
 */
export function preciseTitle(value: Timestamp): string {
  const d = parseUtc(value);
  if (!d) return '';
  return `${dateTimeSeconds(d)} ${MYT} · ${d.toISOString()} UTC`;
}

/** Today's date in Malaysia, as `YYYY-MM-DD`. */
export function todayInMY(): string {
  return dateOnly(new Date(), '');
}

/**
 * `YYYY-MM-DD` for a date `days` before today, in Malaysia.
 *
 * Used for the range presets. Subtracting from the UTC clock and slicing the ISO string — what
 * this replaced — hands back YESTERDAY's Malaysian date at any time before 8am local, so the
 * default range silently excluded the current day for the whole morning shift.
 */
export function daysAgoInMY(days: number): string {
  return dateOnly(new Date(Date.now() - days * 86_400_000), '');
}

/**
 * A coarse elapsed time, for "stalled for …".
 *
 * Two units at most, largest first. An operator looking at a red banner needs to know whether the
 * sync stopped twenty minutes ago or on Monday; the difference between "3d 14h" and "3d 14h 09m"
 * has never changed anyone's next action, and the extra precision costs a glance.
 */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return NONE;

  // Clock skew between the app server and MySQL is small but real, and a banner reading "-1m"
  // undermines the one number the screen exists to convey.
  const total = Math.max(0, Math.floor(ms / 1000));

  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}
